import React, { useState, useEffect } from 'react';

function DataRefreshControls({
  destination,
  refreshingMarketData,
  refreshingUserOrders,
  refreshingWalletTransactions,
  refreshingRegionStats,
  handleRefreshMarketData,
  handleRefreshUserOrders,
  handleRefreshWalletTransactions,
  handleFetchRegionStats,
  progressData
}) {
  const calculateTimeRemaining = (lastFetched, cooldownMinutes) => {
    if (!lastFetched) return 0;
    const elapsed = Math.floor((Date.now() - new Date(lastFetched).getTime()) / 1000);
    return Math.max(0, cooldownMinutes * 60 - elapsed);
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Never';
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  };

  const formatTimeRemaining = (seconds) => {
    if (seconds <= 0) return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const getTimers = () => ({
    marketData: calculateTimeRemaining(destination?.market_data_last_fetched, 5),
    userOrders: calculateTimeRemaining(destination?.user_orders_last_fetched, 20),
    walletTransactions: calculateTimeRemaining(destination?.wallet_transactions_last_fetched, 60),
    regionStats: calculateTimeRemaining(destination?.regional_stats_last_fetched, 60),
  });

  const [refreshTimers, setRefreshTimers] = useState(getTimers);

  useEffect(() => {
    setRefreshTimers(getTimers());
    const interval = setInterval(() => setRefreshTimers(getTimers()), 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination]);
  // Helper function to render progress bar
  const renderProgressBar = (progress) => {
    if (!progress || progress.status !== 'running') return null;

    return (
      <div className="progress-container">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{
              width: `${progress.progress_percentage || 0}%`,
              transition: 'width 0.3s ease'
            }}
          />
        </div>
        <div className="progress-message">
          {progress.message}
        </div>
        {progress.detail && (
          <div className="progress-detail">
            {progress.detail}
          </div>
        )}
      </div>
    );
  };
  const sources = [
    {
      key: 'marketData',
      label: 'Market prices',
      fetched: destination.market_data_last_fetched,
      busy: refreshingMarketData,
      onFetch: handleRefreshMarketData,
      note: !destination.is_structure && 'Station fetches read all regional orders, which can be slow.',
    },
    { key: 'userOrders', label: 'Your orders', fetched: destination.user_orders_last_fetched, busy: refreshingUserOrders, onFetch: handleRefreshUserOrders },
    { key: 'walletTransactions', label: 'Wallet transactions', fetched: destination.wallet_transactions_last_fetched, busy: refreshingWalletTransactions, onFetch: handleRefreshWalletTransactions },
    { key: 'regionStats', label: 'Region statistics', fetched: destination.regional_stats_last_fetched, busy: refreshingRegionStats, onFetch: handleFetchRegionStats },
  ];

  return (
    <section className="panel sources" aria-label="Data sources">
      {sources.map(({ key, label, fetched, busy, onFetch, note }) => {
        const cooldown = refreshTimers[key];
        return (
          <div className="source" key={key}>
            <div className="source-info">
              <div className="source-label">{label}</div>
              <div className="source-age" title={note || undefined}>
                <span className={`source-dot${fetched ? (cooldown > 0 ? '' : ' stale') : ' never'}`} />
                {formatTimeAgo(fetched)}
              </div>
              {renderProgressBar(progressData?.[key])}
            </div>
            <button onClick={onFetch} disabled={busy || cooldown > 0} className="btn">
              {busy ? 'Fetching...' : 'Fetch'}
              {!busy && cooldown > 0 && <span className="btn-cooldown">{formatTimeRemaining(cooldown)}</span>}
            </button>
          </div>
        );
      })}
    </section>
  );
}

export default DataRefreshControls;